import {
  getChunksForRecording,
  deleteChunksForRecording,
  uploadCompleteAction,
  regenerateUploadUrlAction,
  getRecordingMeta,
  deleteRecordingMeta
} from '@/lib/services/recordings';
import type { ClientTelemetryAtUpload } from '@/lib/services/recordings';
import { gatherClientTelemetryAtUpload } from './client-telemetry';
import { uploadFileToUrl, type UploadProgress } from '@/lib/uploads';

// `UploadProgress` used to be defined here; it now lives in the shared upload
// primitive (LF-4771). Re-export it so existing importers (e.g.
// `session/capture/use-inline-capture.ts`) keep resolving it from this module.
export type { UploadProgress };

export type UploadResult =
  | { success: true; recordingId: number }
  | { success: false; error: string };

type ProgressCallback = (progress: UploadProgress) => void;

// ─── Transport seam (LF-3743) ──────────────────────────────────────
//
// The desktop flow uses NextAuth-bound server actions to refresh the S3
// presigned URL and signal upload-complete. The LF-3743 mobile-handoff
// route is unauthenticated — it goes through the public BFF
// (`/api/device-recording/[token]/...`) which forwards a Bearer-token
// derived from the URL path.
//
// Everything else (chunk assembly, XHR PUT with progress + retry,
// 403-refresh, IDB cleanup, fixture-mode bypass) is identical. So we
// abstract the two auth-coupled calls behind a transport interface and
// keep one shared upload pipeline. The default transport preserves the
// existing desktop behaviour — current call sites pass no transport and
// continue working unchanged.

export interface RecordingUploadCompleteBody {
  duration: number;
  fileSize: number;
  /**
   * Telemetry block. `hadResume` / `retryCount` live INSIDE this object,
   * NOT at the top level — both desktop and handoff BE DTOs whitelist the
   * body's top-level keys and reject unknown ones. The pipeline nests
   * them before calling the transport so neither desktopUploadTransport
   * nor the mobile handoff transport needs to reshape.
   */
  clientTelemetryAtUpload?: ClientTelemetryAtUpload;
  /**
   * Truthful audio format derived from the stored chunks' `mimeType`
   * (which was set from `MediaRecorder.mimeType` post-construction).
   * Safety-net for the iOS Safari case where the BE row was created with
   * the wrong format (LF-3780). When this differs from the row's stored
   * format, the BE renames the S3 object + updates the row before
   * invoking Transcribe.
   */
  audioFormat?: 'webm' | 'mp4';
}

export interface RecordingUploadTransport {
  /**
   * Fetch a fresh S3 presigned upload URL for the recording. Called when
   * IndexedDB has no stored URL AND on 403-retry after a PUT.
   */
  regenerateUploadUrl(recordingId: number): Promise<string>;
  /**
   * Signal `upload-complete` to the backend. Idempotent on the BE for
   * handoff recordings; first call for desktop. Throws on failure — the
   * shared pipeline maps the throw into a structured `UploadResult`.
   */
  signalUploadComplete(
    recordingId: number,
    body: RecordingUploadCompleteBody
  ): Promise<void>;
}

/**
 * Default transport — desktop / authenticated app flow. Existing call
 * sites that don't pass a transport hit this and behave exactly as before.
 */
export const desktopUploadTransport: RecordingUploadTransport = {
  async regenerateUploadUrl(recordingId) {
    const result = await regenerateUploadUrlAction(recordingId);
    if (!result.success || !result.data?.uploadUrl) {
      throw new Error(result.error ?? 'Failed to get upload URL');
    }
    return result.data.uploadUrl;
  },
  async signalUploadComplete(recordingId, body) {
    const result = await uploadCompleteAction(recordingId, body);
    if (!result.success) {
      throw new Error(result.error ?? 'Upload completion failed');
    }
  }
};

// ─── S3 Upload ─────────────────────────────────────────────────────
//
// The XHR-with-progress-and-retry that used to live here is now the shared
// `uploadFileToUrl` primitive (LF-4771). This module keeps only the recording-
// specific pipeline around it (chunk assembly, 403-refresh, fixture-mode,
// telemetry). Abort still rejects with an `AbortError`; the pipeline's existing
// `signal?.aborted` checks make that transparent.

// ─── Upload Recording ──────────────────────────────────────────────

export interface UploadRecordingOptions {
  /** Optional progress callback */
  onProgress?: ProgressCallback;
  /** Optional cancellation signal */
  signal?: AbortSignal;
  /**
   * LF-3743 mobile-handoff: true if this upload was resumed from existing
   * IDB chunks (rather than a fresh stop). Threaded to the upload-complete
   * telemetry payload — BE uses it to distinguish recovery from happy path.
   */
  hadResume?: boolean;
  /**
   * LF-3743 mobile-handoff: number of user-initiated retry clicks before this
   * upload finished. Passed in by the caller because the upload pipeline
   * itself doesn't know how many times the user has tapped Retry.
   */
  retryCount?: number;
}

/**
 * Upload a recording to S3 and signal completion.
 * The server recording already exists (created before modal opened).
 * Reads upload URL from IndexedDB meta; fetches a fresh URL if needed.
 *
 * @param recordingId Server recording ID (IndexedDB key for chunks + meta)
 * @param transport   Optional transport. Defaults to the desktop server-action
 *                    flow. LF-3743 mobile-handoff passes a BFF-backed transport.
 * @param onProgressOrSignal  Optional progress callback, AbortSignal, or
 *                            UploadRecordingOptions object.
 * @param maybeSignal Optional AbortSignal (legacy 4-arg shape)
 */
export async function uploadRecording(
  recordingId: number,
  transport:
    | RecordingUploadTransport
    | ProgressCallback = desktopUploadTransport,
  onProgressOrSignal?: ProgressCallback | AbortSignal | UploadRecordingOptions,
  maybeSignal?: AbortSignal
): Promise<UploadResult> {
  // Backwards-compat shim: legacy callers passed `(recordingId, onProgress, signal)`.
  // Detect by argument shape and re-route to the new signature.
  let resolvedTransport: RecordingUploadTransport;
  let onProgress: ProgressCallback | undefined;
  let signal: AbortSignal | undefined;
  let hadResume = false;
  let retryCount = 0;
  if (typeof transport === 'function') {
    resolvedTransport = desktopUploadTransport;
    onProgress = transport;
    signal =
      onProgressOrSignal instanceof AbortSignal
        ? onProgressOrSignal
        : undefined;
  } else {
    resolvedTransport = transport;
    if (typeof onProgressOrSignal === 'function') {
      onProgress = onProgressOrSignal;
      signal = maybeSignal;
    } else if (onProgressOrSignal instanceof AbortSignal) {
      signal = onProgressOrSignal;
    } else if (onProgressOrSignal && typeof onProgressOrSignal === 'object') {
      onProgress = onProgressOrSignal.onProgress;
      signal = onProgressOrSignal.signal;
      hadResume = !!onProgressOrSignal.hadResume;
      retryCount = onProgressOrSignal.retryCount ?? 0;
    } else {
      signal = maybeSignal;
    }
  }

  const log = (step: string, detail?: unknown) =>
    console.log(`[AI Scribe Upload] ${step}`, detail ?? '');

  try {
    // 1. Assemble chunks from IndexedDB
    log('Step 1: Assembling chunks', { recordingId });
    const chunks = await getChunksForRecording(recordingId);
    log('Step 1: Chunks found', { count: chunks.length });
    if (chunks.length === 0) {
      log('Step 1: FAIL, no chunks');
      return { success: false, error: 'No audio data recorded' };
    }

    const meta = await getRecordingMeta(recordingId);
    log('Step 1: Meta', meta);
    const mimeType = meta?.mimeType ?? chunks[0].mimeType;

    // Duration derived from chunk count (each chunk ≈ 1 second)
    const duration = Math.max(1, chunks[chunks.length - 1].totalDuration);

    const blob = new Blob(
      chunks.map((c) => c.data),
      { type: mimeType }
    );
    // Per-chunk read-back sizes — on iOS 18.7+ MediaRecorder Blobs stored in
    // IndexedDB can read back empty (0 bytes) even though they were written
    // non-empty, yielding a 0-byte assembled blob. Logging each chunk's
    // read-back size makes that data loss self-evident on-device (LF-5157).
    log('Step 1: Blob assembled', {
      size: blob.size,
      mimeType,
      duration,
      chunkSizes: chunks.map((c) =>
        c.data instanceof ArrayBuffer
          ? c.data.byteLength
          : (c.data as unknown as Blob).size
      )
    });

    // 2. Get upload URL — use stored URL or fetch a fresh one via transport
    let uploadUrl = meta?.uploadUrl;
    log('Step 2: Stored uploadUrl', { hasUrl: !!uploadUrl });
    if (!uploadUrl) {
      try {
        uploadUrl = await resolvedTransport.regenerateUploadUrl(recordingId);
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Failed to get upload URL'
        };
      }
    }

    // 3. Upload blob to S3 (with retry + progress) — skip in fixture mode
    const isFixtureMode = uploadUrl === 'fixture-mode';
    if (isFixtureMode) {
      log('Step 3: FIXTURE MODE, skipping S3 upload');
    } else {
      log('Step 3: Uploading to S3', { urlLength: uploadUrl.length });
    }

    if (!isFixtureMode) {
      try {
        await uploadFileToUrl(uploadUrl, blob, {
          contentType: mimeType,
          onProgress,
          signal
        });
        log('Step 3: S3 upload SUCCESS');
      } catch (s3Err) {
        log('Step 3: S3 upload FAILED, attempting URL refresh', s3Err);
        if (signal?.aborted) {
          return { success: false, error: 'Upload cancelled' };
        }
        // URL may have expired — get a fresh one via the transport and retry
        let freshUrl: string;
        try {
          freshUrl = await resolvedTransport.regenerateUploadUrl(recordingId);
        } catch (urlErr) {
          log('Step 3: Regenerate URL FAILED', urlErr);
          return {
            success: false,
            error: 'Upload failed and could not get a fresh upload URL'
          };
        }
        try {
          await uploadFileToUrl(freshUrl, blob, {
            contentType: mimeType,
            onProgress,
            signal
          });
          log('Step 3: S3 retry upload SUCCESS');
        } catch (retryErr) {
          log('Step 3: S3 retry FAILED', retryErr);
          return {
            success: false,
            error: signal?.aborted
              ? 'Upload cancelled'
              : 'Upload to storage failed after multiple attempts'
          };
        }
      }
    } // end !isFixtureMode

    // 4. Signal upload complete via the transport.
    // hadResume + retryCount go INSIDE `clientTelemetryAtUpload`, not at
    // the top level — both BE DTOs reject unknown top-level keys.
    //
    // audioFormat is derived from the chunks' `mimeType` (which the
    // recorder hook sets from `MediaRecorder.mimeType` post-construction).
    // BE uses it to reconcile the row's stored format with the truthful
    // bytes — see LF-3780.
    const actualAudioFormat: 'webm' | 'mp4' = mimeType.includes('mp4')
      ? 'mp4'
      : 'webm';

    log('Step 4: Signalling upload-complete', {
      duration,
      fileSize: blob.size,
      audioFormat: actualAudioFormat
    });
    try {
      await resolvedTransport.signalUploadComplete(recordingId, {
        duration,
        fileSize: blob.size,
        audioFormat: actualAudioFormat,
        clientTelemetryAtUpload: {
          ...gatherClientTelemetryAtUpload({ chunkCount: chunks.length }),
          hadResume,
          retryCount
        }
      });
      log('Step 4: upload-complete SUCCESS');
    } catch (completeErr) {
      log('Step 4: upload-complete FAILED', completeErr);
      return {
        success: false,
        error:
          completeErr instanceof Error
            ? completeErr.message
            : 'Upload completion failed'
      };
    }

    // 5. Clean up IndexedDB
    log('Step 5: Cleaning up IndexedDB');
    await deleteChunksForRecording(recordingId);
    await deleteRecordingMeta(recordingId);

    log('DONE: upload complete', { recordingId });
    return { success: true, recordingId };
  } catch (err) {
    log('UNCAUGHT ERROR', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Upload failed'
    };
  }
}
